import { useTranslation } from "react-i18next";
import { Alert, Button, Drawer, Flex, Loader } from "@bdo/cornerstone-ui";
import { UserSearchForm, AccessControlList, useManageAccess } from ".";

export function ManageAccessPanel() {
  const { t } = useTranslation();
  const {
    isOpen,
    setIsOpen,
    workflowInstance,
    entries,
    isLoading,
    isError,
    isSearching,
    isAdding,
    isRemoving,
    addError,
    usersToDelete,
    notificationOverrides,
    temporaryAddedUsers,
    filteredSearchResults,
    searchFormResetRef,
    currentUserEmail,
    handleClose,
    handleSearch,
    handleSelectUser,
    handleSaveChanges,
    handleToggleNotifications,
    handleDeleteUser,
    handleRemoveTemporaryUser,
    setAddError,
  } = useManageAccess();

  return (
    <Drawer
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      shouldCloseOnOverlayClick
      title={t("manageAccess.title", { defaultValue: "Manage access" })}
      customWidth="24rem"
      padding="md"
    >
      <div
        className="flex flex-col w-full h-full gap-md"
        data-testid="manage-access-panel"
      >
        <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 flex flex-col gap-md px-[3px]">
          <div className="text-base">
            <span className="text-mono-darker">
              {t("manageAccess.description", {
                defaultValue: "Who has access to",
              })}
            </span>{" "}
            <span className="text-mono-darker font-bold">
              {workflowInstance?.workflowInstanceName || "Workflow Name"}
            </span>
          </div>

          <UserSearchForm
            filteredResults={filteredSearchResults}
            isSearching={isSearching}
            isDisabled={isAdding}
            addError={addError}
            resetRef={searchFormResetRef}
            onSearch={handleSearch}
            onSelectUser={handleSelectUser}
            onClearError={() => setAddError(null)}
          />

          {isLoading && (
            <div
              className="flex justify-center py-xl"
              data-testid="manage-access-loader"
            >
              <Loader size={32} />
            </div>
          )}

          {isError && (
            <Alert
              data-testid="manage-access-error-alert"
              variant="error"
              title={t("manageAccess.errors.loadFailed", {
                defaultValue: "Failed to load access control list",
              })}
              description={t("manageAccess.errors.loadFailedDescription", {
                defaultValue: "Please try refreshing the page.",
              })}
              isCloseable={false}
            />
          )}

          {!isLoading &&
            !isError &&
            (entries.length > 0 || temporaryAddedUsers.length > 0) && (
              <AccessControlList
                entries={entries}
                currentUserEmail={currentUserEmail}
                usersToDelete={usersToDelete}
                notificationOverrides={notificationOverrides}
                temporaryAddedUsers={temporaryAddedUsers}
                onToggleNotifications={handleToggleNotifications}
                onDeleteUser={handleDeleteUser}
                onRemoveTemporaryUser={handleRemoveTemporaryUser}
              />
            )}
        </div>
        <div className="flex-shrink-0">
          <Flex justify="between" align="center">
            <Button
              onClick={handleClose}
              variant="secondary"
              size="md"
              className="min-w-[112px]"
              data-testid="manage-access-cancel-button"
            >
              {t("manageAccess.cancelButton", { defaultValue: "Cancel" })}
            </Button>
            <Button
              onClick={handleSaveChanges}
              variant="primary"
              size="md"
              className="min-w-[112px]"
              data-testid="manage-access-save-button"
              disabled={
                isAdding ||
                isRemoving ||
                (temporaryAddedUsers.length === 0 && usersToDelete.length === 0)
              }
            >
              {isAdding || isRemoving
                ? t("manageAccess.savingButton", { defaultValue: "Saving..." })
                : t("manageAccess.saveButton", {
                    defaultValue: "Save changes",
                  })}
            </Button>
          </Flex>
        </div>
      </div>
    </Drawer>
  );
}
